// utils/collectionContext.ts — pure helpers for the agency context switch (mig 1230, M-AG-001).
import type { CollectionContext } from '~/types/collections'
import { translateSupabaseError } from '~/utils/supabase-error'

/** localStorage key written right after a successful switch; other tabs listen for it. */
export const CONTEXT_STORAGE_KEY = 'hd:collection-context'

export interface ContextPing { clinic_id: string; at: number }

/**
 * A tab must reload before any write when the clinic it loaded is no longer the
 * profile's clinic. Unknown on either side → never reload (profile still loading,
 * logged out, RPC error): a false reload loop would be worse than a late one.
 */
export function shouldReloadForContext(loadedClinicId: string | null, currentClinicId: string | null): boolean {
  if (!loadedClinicId || !currentClinicId) return false
  return loadedClinicId !== currentClinicId
}

export function writeContextPing(storage: Pick<Storage, 'setItem'>, clinicId: string, now: number = Date.now()): void {
  const ping: ContextPing = { clinic_id: clinicId, at: now }
  storage.setItem(CONTEXT_STORAGE_KEY, JSON.stringify(ping))
}

export function parseContextPing(raw: string | null): ContextPing | null {
  if (!raw) return null
  try {
    const v = JSON.parse(raw)
    if (v && typeof v.clinic_id === 'string' && typeof v.at === 'number') return { clinic_id: v.clinic_id, at: v.at }
    return null
  } catch {
    return null
  }
}

export function contextOptionLabel(c: CollectionContext): string {
  return c.is_home ? `${c.clinic_name} (agencia)` : c.clinic_name
}

// RAISE EXCEPTION text literals from switch_collection_context / get_my_collection_contexts
// (mig 1230, lines 337/397/398/409/414) and get_agency_overview (mig 1233, M-AG-007) — none of
// these match translateSupabaseError's KNOWN substrings or carry code P0001, so without this
// mapping it would fall through to its final "return rawMsg verbatim" branch and leak the bare
// code (e.g. "not_agency_member") to the user.
const CONTEXT_SWITCH_ERRORS: Record<string, string> = {
  not_agency_member:    'Tu usuario no pertenece a una agencia de cobranza.',
  membership_suspended: 'Tu acceso a la agencia está suspendido.',
  no_active_engagement: 'Esa clínica ya no tiene un contrato activo con tu agencia.',
  // get_agency_overview() (mig 1233): the member fetched it from inside a client clinic or a
  // stale context — the «Resumen» tab only exists at the agency home (P4-R8 carry).
  not_at_agency_home:   'El resumen solo está disponible en tu agencia.',
  // Part 5 (mig 1268 / 1267)
  agency_suspended:     'Tu agencia está suspendida. Revisa «Plan y pagos».',
  agency_admin_only:    'Solo la administradora de la agencia puede hacer esto.',
}

/** Spanish toast copy for a switch_collection_context / get_my_collection_contexts failure.
 *  Any code/message not in CONTEXT_SWITCH_ERRORS defers to the generic translator. */
export function translateCollectionContextError(e: unknown): string {
  const raw = typeof e === 'string' ? e : (e as { message?: unknown })?.message
  if (typeof raw === 'string' && raw in CONTEXT_SWITCH_ERRORS) return CONTEXT_SWITCH_ERRORS[raw]
  return translateSupabaseError(e)
}
